import { Directive, effect, ElementRef, inject, input } from '@angular/core';

/**
 * Bug 35901 / WI 35847: one-way value binding for a hand-bound `<input type="number">` that writes the box only
 * when the bound number differs from what the box's own text already means. A plain `[value]` echoes the parsed
 * number back mid-edit (Backspace on '25.05' leaves '25.0', parseFloat gives 25, and `[value]` rewrites the box to
 * '25', so the next digit makes 258). ngModel's number accessor never echoed a value the view itself emitted; this
 * keeps that. Use `[upsNumberValue]="x"` in place of `[value]="x"`, paired with `(input)="... numberOrNull($event)"`.
 * Shared (@ups/xplat/web/features): never copy it into a feature lib.
 */
@Directive({ selector: 'input[upsNumberValue]', standalone: true })
export class NumberValueDirective {
  readonly upsNumberValue = input<number | string | null | undefined>();
  private readonly box =
    inject<ElementRef<HTMLInputElement>>(ElementRef).nativeElement;

  constructor() {
    effect(() => {
      const value = this.upsNumberValue();
      const next = value == null || value === '' ? null : Number(value);
      // An unparseable entry ('-', '1e') reads as '' on a number box: leave the user's text alone.
      const shown = this.box.value === '' ? null : parseFloat(this.box.value);
      if (shown === next || (next !== null && Number.isNaN(next))) return;
      this.box.value = next === null ? '' : String(next);
    });
  }
}
